import { Button, Grid, Stack, Tab, Tabs } from '@mui/material';
import React, { useState, useEffect, useCallback } from 'react';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../store/store';
import { getActiveProviders, getProvidersBasedOnPermission, getTCCRoleProviders, getLicensedRoleProviders } from '../../store/provider.slice';
import Authorization from '../../utils/Authorization';
import { useNavigate } from 'react-router';
import {
    getCustomDashboardTabs,
    getStartingRoleWidgets,
    setSelectedTab,
    setSelectedTabModal,
    setShowWidgetsMenu,
    setWidgetsToRender,
    setIsWidgetExisting,
} from '../../store/smartDashboard.slice';
import { RootState } from '../../reducers';
import { allWidgetsList } from './Shared/AllWidgetsList';
import SetVisibleWidgetsDialog from './SetVisibleWidgetsDialog/SetVisibleWidgetsDialog';
import { useHealthPlanContext } from '../../hooks/healthPlanContext';
import { useHedisMetricsContext } from '../../hooks/hedisMetricsContext';
import { getRequestType } from '../../store/request.slice';
import { fetchPopulationList } from '../../store/adminMembers.slice';
import { fetchPopulations, fetchAnalyticPopulations } from '../../store/population.slice';
import Loading from '../Shared/Loading';
import AlertDialog from '../AlertDialog/AlertDialog';
import { setIsLoading } from '../../store/shared.slice';

interface WrapperProps<T = unknown> {
    comp: React.ReactElement<T>;
    hasElevatedFilterAccess: boolean;
}

const Wrapper = <T extends object>({ comp, hasElevatedFilterAccess }: WrapperProps<T>) => {
    return React.cloneElement(comp, {
        hasElevatedFilterAccess,
    } as unknown as Partial<T>);
};

const SmartDashboard = (props) => {
    const auth = new Authorization();
    const history = useNavigate();
    const dispatch = useAppDispatch();
    const { customTabsList, selectedTab, widgetsToRender, isWidgetExisting, widgetFromMetricSummary } = useSelector((state: RootState) => state.smartDashboard);
    const { careTeams: teams } = useSelector((state: RootState) => state.careTeams);
    const { setStatus, setTeam, loadedTeam, setLoadedTeam } = useHedisMetricsContext();
    const { getHealthPlans } = useHealthPlanContext();
    const [isFetched, setIsFetched] = useState<boolean>(false);
    const [showWidgetAlert, setShowWidgetAlert] = useState<boolean>(false);
    const tabChangeHandler = (e, newValue) => {
        dispatch(setSelectedTab(newValue));
    };

    const showWidgetsMenuHandler = (selectedTab) => {
        dispatch(setSelectedTabModal(selectedTab));
        dispatch(setShowWidgetsMenu(true));
    };

    const initialPage = useCallback(async () => {
        if (isFetched) {
            return;
        }

        dispatch(setShowWidgetsMenu(false));
        dispatch(fetchPopulationList());
        dispatch(fetchPopulations());
        dispatch(fetchAnalyticPopulations());

        if (!isWidgetExisting) {
            setTimeout(() => {
                setShowWidgetAlert(true);
                dispatch(setIsLoading(false));
            }, 1500);
        }

        setIsFetched(true);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isFetched, setIsFetched, setStatus]);

    useEffect(() => {
        initialPage();
    }, [initialPage]);

    useEffect(() => {
        const loggedInTeamId = auth.getDefaultTeamId('');
        if (Boolean(loggedInTeamId) && !loadedTeam) {
            const teamInfo = teams.find((t) => t.Id === loggedInTeamId);
            if (Boolean(teamInfo)) {
                setTeam(loggedInTeamId);
                setLoadedTeam(true);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [teams]);

    useEffect(() => {
        if (customTabsList?.length > 0) {
            const newWidgetsToRender = customTabsList.map((tab) =>
                tab.ApplicableWidgets.map((widget, index) => {
                    const component = allWidgetsList.find((w) => w.name === widget.Name)?.component;
                    if (!component) return [];
                    return (
                        <Wrapper
                            key={`${tab.TabName}-${widget.Name}-${index}`}
                            comp={component}
                            hasElevatedFilterAccess={widget.ElevatedFilterAccess ?? false}
                        />
                    );
                })
            );

            dispatch(setWidgetsToRender(newWidgetsToRender));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [customTabsList]);

    useEffect(() => {
        dispatch(getActiveProviders(true));
        dispatch(getProvidersBasedOnPermission('View onboarding', true));
        dispatch(getTCCRoleProviders(true));
        dispatch(getLicensedRoleProviders(true));
        dispatch(getRequestType());
        dispatch(getStartingRoleWidgets(auth.Role));
        dispatch(getCustomDashboardTabs(auth.UserId, auth.Role));
        getHealthPlans();
        document.title = props.title;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const navigateToMetrics = () => {
        history(`/provider/metricsummary`);
    };

    const handleOnConfirmed = () => {
        dispatch(setIsWidgetExisting(true));
        dispatch(setShowWidgetsMenu(true));
        setShowWidgetAlert(false);
    };

    return (
        <>
            <Loading message="Loading..." />
            <SetVisibleWidgetsDialog />
            <Grid container spacing={2}>
                <Grid
                    size={12}
                    textAlign={'right'}
                    p={2}
                    marginBottom={2}
                    position="sticky"
                    top={0}
                    width={'100vw'}
                    zIndex={1200}
                    sx={{ background: '#ffffff' }}
                >
                    <Stack direction="row" justifyContent="space-between">
                        <Tabs value={selectedTab} onChange={tabChangeHandler}>
                            {customTabsList?.map((item, index) => {
                                return <Tab key={index} value={index} label={item.tabName} />;
                            })}
                        </Tabs>
                        <Stack direction="row">
                            <Button
                                variant="text"
                                sx={{ marginLeft: 'auto', paddingRight: '15px' }}
                                onClick={() => {
                                    navigateToMetrics();
                                }}
                            >
                                Back To Metrics
                            </Button>
                            <Button sx={{ marginLeft: 'auto' }} onClick={() => showWidgetsMenuHandler(selectedTab)}>
                                Customize Dashboard
                            </Button>
                        </Stack>
                    </Stack>
                </Grid>
                {widgetsToRender?.map((list, index) => {
                    return selectedTab === index && list;
                })}
            </Grid>

            <AlertDialog
                open={showWidgetAlert}
                title="Confirmation"
                cancelText="Cancel"
                okText="Continue"
                onConfirm={() => handleOnConfirmed()}
                onClose={() => {
                    dispatch(setIsWidgetExisting(true));
                    dispatch(setShowWidgetsMenu(false));
                    setShowWidgetAlert(false);
                }}
                message={`Widget ${widgetFromMetricSummary} has not been added to your dashboard. Would you like to add it now?`}
            />
        </>
    );
};

export default SmartDashboard;
